/**
 * The small parts every creator screen is built from: headings, the big single
 * field, buttons, chips, choice rows and the clue meter.
 */
import { useTranslation } from "react-i18next";
import Icon from "./Icon";
import { C, FONT, SERIF, GRADIENT, SHADOW, RADIUS, TINT } from "./tokens";
import { MIN_CLUES, RECOMMENDED_CLUES, MAX_CLUES } from "../constants";

/**
 * The big question. `accent` is the part in italic brand colour, `tail` whatever
 * follows it ("This is what *Roberto* sees").
 */
export function Heading({ children, accent, tail, size = 34 }) {
  return (
    <h1 style={{ margin: "16px 0 0", fontSize: size, lineHeight: 1.08, fontWeight: 800, letterSpacing: "-0.035em", color: C.ink }}>
      {children}
      {accent ? <> <em style={{ fontStyle: "italic", color: C.accent }}>{accent}</em></> : null}
      {tail ? <> {tail}</> : null}
    </h1>
  );
}

export function Sub({ children }) {
  return <p style={{ margin: "12px 0 0", fontSize: 15, lineHeight: 1.5, color: C.body }}>{children}</p>;
}

/** The small pill above the heading — clue number, "the reveal", "all set". */
export function StepChip({ badge, label, tone = "accent" }) {
  const bg = tone === "ok" ? C.ok : C.accent;
  return (
    <div style={{ display: "inline-flex", alignItems: "center", gap: 7, padding: "6px 12px 6px 7px", borderRadius: 999, background: C.surface, boxShadow: `0 6px 16px -8px ${tone === "ok" ? "rgba(92,140,107,0.45)" : TINT.accent(0.35)}` }}>
      <span style={{ width: 20, height: 20, borderRadius: 999, background: bg, color: C.onAccent, fontSize: 11, fontWeight: 800, display: "grid", placeItems: "center" }}>
        {badge === "check" ? <Icon name="check" size={11} color={C.onAccent} strokeWidth={3.6} /> : badge}
      </span>
      <span style={{ fontSize: 12, fontWeight: 800, letterSpacing: "0.12em", textTransform: "uppercase", color: tone === "ok" ? C.okDeep : C.accent }}>{label}</span>
    </div>
  );
}

/**
 * The one thing the screen asks for. A single field in a focused card, big enough
 * to be the only thing on screen.
 */
export function BigField({ id, label, value, onChange, placeholder, multiline = false, rows = 2, fontSize = 26, serif = false, autoFocus = true, inputMode, count }) {
  const common = {
    id,
    value,
    placeholder,
    autoFocus,
    inputMode,
    onChange: (e) => onChange(e.target.value),
    style: {
      width: "100%", boxSizing: "border-box", marginTop: 6, border: "none", outline: "none",
      background: "transparent", padding: 0, resize: "none",
      fontFamily: serif ? SERIF : FONT,
      fontStyle: serif ? "italic" : "normal",
      fontSize, lineHeight: multiline ? 1.32 : 1.15,
      fontWeight: serif ? 500 : 800, letterSpacing: serif ? 0 : "-0.025em", color: C.ink,
    },
  };
  return (
    <div style={{ marginTop: 24, background: C.surface, borderRadius: RADIUS.field, padding: "18px 20px 16px", border: `2px solid ${C.accent}`, boxShadow: SHADOW.focus }}>
      <label htmlFor={id} style={{ display: "block", fontSize: 11, fontWeight: 800, letterSpacing: "0.12em", textTransform: "uppercase", color: C.label }}>{label}</label>
      {multiline ? <textarea rows={rows} {...common} /> : <input type="text" {...common} />}
      {count ? (
        <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 4 }}>
          <span style={{ fontSize: 11, fontWeight: 600, color: C.label }}>{count}</span>
        </div>
      ) : null}
    </div>
  );
}

export function PrimaryButton({ children, onClick, disabled, icon = "arrow", testId }) {
  return (
    <button
      type="button"
      data-testid={testId}
      onClick={onClick}
      disabled={disabled}
      style={{
        height: 58, borderRadius: RADIUS.button, border: "none", width: "100%",
        background: disabled ? C.disabledBg : GRADIENT.primary,
        color: disabled ? C.faint : C.onAccent,
        fontFamily: FONT, fontSize: 17, fontWeight: 700, cursor: disabled ? "not-allowed" : "pointer",
        boxShadow: disabled ? "none" : SHADOW.primary,
        display: "flex", alignItems: "center", justifyContent: "center", gap: 9,
      }}
    >
      {children}
      {icon ? <Icon name={icon} size={18} color={disabled ? C.faint : C.onAccent} strokeWidth={2.5} /> : null}
    </button>
  );
}

export function SecondaryButton({ children, onClick, icon, testId, disabled }) {
  return (
    <button
      type="button"
      data-testid={testId}
      onClick={onClick}
      disabled={disabled}
      style={{
        height: 56, borderRadius: RADIUS.button, border: "none", width: "100%",
        background: C.surface, color: disabled ? C.label : C.accentDeep,
        fontFamily: FONT, fontSize: 16, fontWeight: 700, cursor: disabled ? "not-allowed" : "pointer",
        boxShadow: `0 1px 2px ${TINT.shadow(0.06)}, 0 12px 26px -14px ${TINT.shadow(0.35)}`,
        display: "flex", alignItems: "center", justifyContent: "center", gap: 9,
      }}
    >
      {icon ? <Icon name={icon} size={17} color={disabled ? C.label : C.accentDeep} strokeWidth={2.5} /> : null}
      {children}
    </button>
  );
}

/** A dashed, optional action — "add a hint", "name this clue", "add a photo". */
export function GhostPill({ children, onClick, icon = "plus", testId }) {
  return (
    <button
      type="button"
      data-testid={testId}
      onClick={onClick}
      style={{
        height: 40, padding: "0 14px 0 11px", borderRadius: 999, border: `1.5px dashed ${C.dash}`,
        background: TINT.glass(0.6), display: "inline-flex", alignItems: "center", gap: 6,
        fontFamily: FONT, fontSize: 13, fontWeight: 700, color: C.accentDeep, cursor: "pointer",
      }}
    >
      <Icon name={icon} size={14} color={C.accentDeep} strokeWidth={2.4} />
      {children}
    </button>
  );
}

/** An optional detail that has been filled in: shows what was written, tap to edit. */
export function ValuePill({ label, value, onClick, icon, testId }) {
  return (
    <button
      type="button"
      data-testid={testId}
      onClick={onClick}
      style={{
        maxWidth: "100%", minHeight: 40, padding: "8px 12px 8px 11px", borderRadius: 999, border: "none",
        background: C.accentSoft, display: "inline-flex", alignItems: "center", gap: 7,
        fontFamily: FONT, fontSize: 13, fontWeight: 700, color: C.accentDeep, cursor: "pointer", textAlign: "left",
      }}
    >
      {icon ? <Icon name={icon} size={14} color={C.accentDeep} strokeWidth={2.4} /> : null}
      <span style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
        {label} · <span style={{ fontWeight: 600, color: C.ink }}>{value}</span>
      </span>
      <Icon name="pencil" size={13} color={C.accentDeep} strokeWidth={2.4} />
    </button>
  );
}

/** A tappable suggestion on the question screen. */
export function Chip({ children, onClick, selected }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={!!selected}
      style={{
        height: 38, padding: "0 14px", borderRadius: 999,
        border: selected ? `1.5px solid ${C.accent}` : "none",
        background: selected ? C.accentSoft : C.surface,
        boxShadow: selected ? "none" : `0 6px 14px -8px ${TINT.shadow(0.3)}`,
        fontFamily: FONT, fontSize: 13, fontWeight: 700, color: selected ? C.accentDeep : C.muted, cursor: "pointer",
      }}
    >
      {children}
    </button>
  );
}

/** A radio row with artwork — used by the celebration and seal screens. */
export function ChoiceRow({ id, name, checked, onChange, art, title, description, badge }) {
  return (
    <label
      htmlFor={id}
      style={{
        display: "flex", alignItems: "center", gap: 13, padding: "10px 16px 10px 10px", borderRadius: RADIUS.card,
        background: C.surface, cursor: "pointer",
        border: checked ? `2px solid ${C.accent}` : `1.5px solid ${C.line}`,
        boxShadow: checked ? `0 0 0 4px ${TINT.accent(0.10)}, 0 16px 32px -20px ${TINT.accent(0.45)}` : `0 12px 26px -20px ${TINT.shadow(0.35)}`,
      }}
    >
      {art}
      <span style={{ flexGrow: 1, minWidth: 0 }}>
        <span style={{ display: "flex", alignItems: "center", gap: 7 }}>
          <span style={{ fontSize: 15, fontWeight: 800, color: C.ink }}>{title}</span>
          {badge}
        </span>
        {description ? <span style={{ display: "block", marginTop: 1, fontSize: 12, lineHeight: 1.4, color: C.soft }}>{description}</span> : null}
      </span>
      <input
        id={id}
        type="radio"
        name={name}
        checked={checked}
        onChange={() => onChange()}
        style={{ flexShrink: 0, width: 22, height: 22, margin: 0, accentColor: C.accent }}
      />
    </label>
  );
}

export function ProBadge() {
  const { t: tr } = useTranslation();
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 3, padding: "2px 7px", borderRadius: 999, background: C.badge, color: C.white, fontSize: 9, fontWeight: 800, letterSpacing: "0.08em" }}>
      <Icon name="lock" size={8} color={C.white} strokeWidth={3} />
      {tr("app.creator.pro")}
    </span>
  );
}

/**
 * The 10-notch clue meter. The first MIN_CLUES notches are tinted as needed and
 * the RECOMMENDED one is outlined, so the limits are visible from clue one
 * without anyone having to read a rule (decision D2).
 */
export function ClueMeter({ written, headline, note, hint }) {
  return (
    <div style={{ alignSelf: "stretch", marginTop: 16 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 12 }}>
        <span style={{ fontSize: 13, fontWeight: 800, color: C.ink }}>{headline}</span>
        <span style={{ fontSize: 12, fontWeight: 700, color: C.faint, textAlign: "right" }}>{note}</span>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: `repeat(${MAX_CLUES}, minmax(0, 1fr))`, gap: 4, marginTop: 8 }}>
        {Array.from({ length: MAX_CLUES }, (_, i) => {
          const n = i + 1;
          const done = n <= written;
          const needed = n <= MIN_CLUES;
          const suggested = n === RECOMMENDED_CLUES;
          return (
            <span
              key={n}
              style={{
                height: 6, borderRadius: 3,
                background: done ? GRADIENT.primary : needed ? C.needed : suggested ? C.suggested : C.optional,
                boxShadow: !done && suggested ? `inset 0 0 0 1.5px ${C.dash}` : "none",
              }}
            />
          );
        })}
      </div>
      {hint ? <p style={{ margin: "8px 0 0", fontSize: 12, lineHeight: 1.45, color: C.faint }}>{hint}</p> : null}
    </div>
  );
}
